"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";

/** Kein `lucide-react` in dieser Datei — weniger Turbopack/HMR-Probleme mit Icon-Chunks. */
function SvgClose({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden
		>
			<path d="M18 6 6 18" />
			<path d="m6 6 12 12" />
		</svg>
	);
}

function SvgCalendar({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden
		>
			<path d="M8 2v4" />
			<path d="M16 2v4" />
			<rect width="18" height="18" x="3" y="4" rx="2" />
			<path d="M3 10h18" />
		</svg>
	);
}

function NotdienstHeadingIcon({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden
		>
			<path d="M11 2v2" />
			<path d="M5 2v2" />
			<path d="M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1" />
			<path d="M8 15a6 6 0 0 0 12 0v-3" />
			<circle cx="20" cy="10" r="2" />
		</svg>
	);
}

/**
 * Statischer Hinweis: Notdienst 01.–03.05., 09:00–18:00 Uhr.
 * Performance: kein backdrop-blur, kein farbiges Vollbild-Overlay (nur die Karte), keine zoom-Animation.
 */
/** `false` = Banner aus. Wieder an: `true` setzen, unten `EVENT_YEAR` / Tage / `HIDE_AFTER` anpassen. */
const NOTDIENST_BANNER_ENABLED = false;

const EVENT_YEAR = 2026;

const HIDE_AFTER = new Date(EVENT_YEAR, 4, 3, 23, 59, 59, 999);

function isTestMode(): boolean {
	return process.env.NEXT_PUBLIC_NOTDIENST_BANNER_TEST === "1";
}

function shouldShowByDate(now: Date): boolean {
	if (isTestMode()) return true;
	return now <= HIDE_AFTER;
}

export default function HomeNotdienstNotice() {
	const pathname = usePathname();
	const [mounted, setMounted] = useState(false);
	const [visible, setVisible] = useState(false);
	const [userDismissed, setUserDismissed] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!NOTDIENST_BANNER_ENABLED) return;
		if (!mounted) return;

		const now = new Date();
		if (!shouldShowByDate(now)) {
			setVisible(false);
			return;
		}

		if (pathname !== "/") {
			setVisible(false);
			return;
		}

		if (userDismissed) {
			setVisible(false);
			return;
		}

		const t = window.setTimeout(() => setVisible(true), 200);
		return () => window.clearTimeout(t);
	}, [mounted, pathname, userDismissed]);

	const handleClose = () => {
		setUserDismissed(true);
		setVisible(false);
	};

	useEffect(() => {
		if (!visible) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setUserDismissed(true);
				setVisible(false);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [visible]);

	if (!NOTDIENST_BANNER_ENABLED) {
		return null;
	}

	if (!mounted || !visible) {
		return null;
	}

	if (!shouldShowByDate(new Date())) {
		return null;
	}

	const yStr = String(EVENT_YEAR);
	const d1 = `01.05.${yStr}`;
	const d2 = `02.05.${yStr}`;
	const d3 = `03.05.${yStr}`;

	const scheduleRows = [
		{ dayNum: "01", weekday: "Fr", date: d1, time: "09:00–18:00" },
		{ dayNum: "02", weekday: "Sa", date: d2, time: "09:00–18:00" },
		{ dayNum: "03", weekday: "So", date: d3, time: "09:00–18:00" },
	];

	const modal = (
		<div
			className="fixed inset-0 z-[10000] flex items-center justify-center px-3 py-4 sm:p-6 bg-transparent pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
			role="presentation"
			onClick={handleClose}
		>
			<div
				className="relative z-[10001] w-full max-w-[min(100%,36rem)] sm:max-w-2xl lg:max-w-3xl rounded-2xl shadow-xl border-2 border-[#68353f] bg-white dark:bg-[#1f1216] max-h-[min(92vh,100dvh)] overflow-y-auto overscroll-contain touch-pan-y select-none"
				role="dialog"
				aria-modal="true"
				aria-labelledby="notdienst-banner-title"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					type="button"
					onClick={handleClose}
					className="absolute top-2 right-2 sm:top-4 sm:right-4 min-w-[44px] min-h-[44px] w-11 h-11 flex items-center justify-center rounded-full bg-white dark:bg-[#2a1519] hover:bg-[#ece7e5] dark:hover:bg-[#3a252d] z-30 shadow-md border border-[#68353f]/30 dark:border-[#68353f]/50"
					aria-label="Hinweis schließen"
				>
					<SvgClose className="w-5 h-5 text-[#68353f] dark:text-rose-100" />
				</button>

				<div className="relative z-10 rounded-2xl p-5 pt-14 sm:p-8 sm:pt-8">
					<div className="text-center mb-6 sm:mb-8">
						<div className="inline-flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 bg-[#68353f]/12 dark:bg-[#68353f]/35 rounded-full mb-3">
							<NotdienstHeadingIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#68353f] dark:text-rose-200" />
						</div>
						<h2
							id="notdienst-banner-title"
							className="text-lg sm:text-2xl font-bold text-[#68353f] dark:text-rose-100 font-[family-name:var(--font-heading)] tracking-tight"
						>
							Zahnärztlicher Notdienst
						</h2>
						<p className="text-sm sm:text-base text-[#5c2f38] dark:text-rose-200/90 mt-2 font-medium max-w-xl mx-auto leading-snug">
							01., 02. und 03. Mai {yStr} · jeweils 09:00–18:00 Uhr
						</p>
					</div>

					<div
						className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4"
						role="list"
					>
						{scheduleRows.map((row) => (
							<div
								key={row.date}
								role="listitem"
								className="flex flex-col items-center text-center rounded-xl bg-[#f8f3ef] dark:bg-[#2a1519] border border-[#68353f]/20 dark:border-[#68353f]/40 px-4 py-4 sm:py-5 tabular-nums"
							>
								<div
									className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-[#68353f] text-lg sm:text-xl font-bold text-white shadow-sm mb-2"
									aria-hidden
								>
									{row.dayNum}
								</div>
								<span className="text-xs font-semibold uppercase tracking-wider text-[#68353f]/80 dark:text-rose-300/90 mb-1">
									{row.weekday}
								</span>
								<div className="flex items-center gap-1.5 text-gray-800 dark:text-gray-100 mb-1">
									<SvgCalendar className="w-4 h-4 text-[#68353f] dark:text-rose-300 shrink-0" />
									<span className="text-sm sm:text-base font-semibold">{row.date}</span>
								</div>
								<span className="text-sm font-semibold text-[#68353f] dark:text-rose-200">
									{row.time}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);

	return createPortal(modal, document.body);
}
